import { FormFieldObject } from "@/lib/stores/objectsStore";
import { decodeHtml } from "@/lib/utils";
import { TypeElementInfo, GlossaryItemInfo, DialogInfo, UrlValue } from "./objectFormTypes";

/**
 * Check if content contains HTML tags
 */
export const containsHtml = (content: any): boolean => {
  if (typeof content !== 'string') return false;
  
  const hasHtmlTags = /<[^>]*>/.test(content);
  const hasEscapedHtml = /&lt;|&gt;|&amp;|&quot;|&#39;/.test(content);
  
  
  
  return hasHtmlTags || hasEscapedHtml;
};

/**
 * Check if field type is text-like
 */
export const isTextLikeField = (fieldType: string): boolean => {
  const textTypes = ['text', 'textarea', 'title', 'description', 'content', 'body', 'textfield'];
  const normalizedType = fieldType?.toLowerCase().trim();
  
  const exactMatch = textTypes.includes(normalizedType);
  const containsText = normalizedType?.includes('text');
  const containsArea = normalizedType?.includes('area');
  
  return exactMatch || containsText || containsArea;
};

/**
 * Convert field value to proper format for wizard elements
 */
export const getFormattedFieldValue = (field: FormFieldObject, value: any): any => {
  if (field.type === "url") {
    // Convert to urlValue object structure
    if (typeof value === "string") {
      // Handle legacy string format (split by |||)
      const parts = value.split("|||");
      const result: UrlValue = {
        url: parts[0] || "",
        text: parts[1] || parts[0] || ""
      };
      return result;
    } else if (value && typeof value === "object" && value.url !== undefined) {
      // Already in urlValue format
      return value;
    } else {
      // Default empty urlValue
      return { url: "", text: "" };
    }
  }
  return value;
};

/**
 * Get type element name and description by ID
 */
export const getTypeElementInfo = (
  typeId: string, 
  types: any[]
): TypeElementInfo => {
  for (const type of types) {
    if (type.data) {
      const element = type.data.find((el: any) => el.id === typeId);
      if (element) {
        return {
          name: element.name,
          description: element.description || `Type: ${type.name}`
        };
      }
    }
  }
  return { name: `Unknown Type (${typeId})`, description: "Type element not found" };
};

/**
 * Get glossary item name and description by ID
 */
export const getGlossaryItemInfo = (
  itemId: string, 
  glossaryItems: any[]
): GlossaryItemInfo => {
  if (!glossaryItems || !Array.isArray(glossaryItems)) {
    return { name: `Unknown Item (${itemId})`, description: "Glossary items not available" };
  }
  const item = glossaryItems.find(item => item.id === itemId);
  if (item) {
    // Try to find a description field in the item data
    let description = "No description available";
    for (const section of item.data) {
      for (const field of section.fields) {
        if (field.label.toLowerCase().includes('description') || field.id === 'description') {
          if (field.value && typeof field.value === 'string' && field.value.trim()) {
            description = field.value;
            break;
          }
        }
      }
      if (description !== "No description available") break;
    }
    
    return {
      name: item.name,
      description: description
    };
  }
  return { name: `Unknown Item (${itemId})`, description: "Glossary item not found" };
};

/**
 * Get dialog name and description by ID
 */
export const getDialogInfo = (
  dialogId: string, 
  dialogs: any[]
): DialogInfo => {
  if (!dialogs || !Array.isArray(dialogs)) {
    return { name: `Unknown Dialog (${dialogId})`, description: "Dialogs not available", dialog_key: "" };
  }
  const dialog = dialogs.find(d => d.id === dialogId);
  if (dialog) {
    return {
      name: dialog.name,
      description: dialog.description || "No description available",
      dialog_key: dialog.dialog_key || ""
    };
  }
  return { name: `Unknown Dialog (${dialogId})`, description: "Dialog not found", dialog_key: "" };
};

/**
 * Clean up invalid glossary item references from field values
 */
export const cleanupInvalidGlossaryReferences = (
  fieldValue: any,
  availableGlossaryItems: any[]
): any => {
  if (!fieldValue) return fieldValue;
  
  // Handle comma-separated string format (current)
  if (typeof fieldValue === 'string' && fieldValue.trim()) {
    const itemIds = fieldValue.split(',').map(id => id.trim()).filter(id => id);
    const validIds = itemIds.filter(id => 
      availableGlossaryItems && availableGlossaryItems.some(item => item.id === id)
    );
    return validIds.length > 0 ? validIds.join(',') : '';
  }
  
  // Handle array format (legacy)
  if (Array.isArray(fieldValue)) {
    const validIds = fieldValue.filter(id => 
      availableGlossaryItems && availableGlossaryItems.some(item => item.id === id)
    );
    return validIds.length > 0 ? validIds : [];
  }
  
  // Handle single value
  if (typeof fieldValue === 'string' || typeof fieldValue === 'number') {
    const id = String(fieldValue);
    const isValid = availableGlossaryItems && availableGlossaryItems.some(item => item.id === id);
    return isValid ? fieldValue : '';
  }
  
  return fieldValue;
};

/**
 * Check if a dropdown field should be treated as optional due to no available options
 */
export const shouldTreatAsOptional = (
  field: FormFieldObject,
  availableGlossaryItems?: any[],
  availableTypes?: any[]
): boolean => {
  if (!field.required) return false; // Already optional
  
  // Check glossaryItem fields
  if (field.type === 'glossaryItem') {
    if (!availableGlossaryItems || availableGlossaryItems.length === 0) {
      return true; // No items available, treat as optional
    }
    
    // If field has filter options, check if any items match the filter (by blueprint/template)
    if (field.filterOptions && field.filterOptions.length > 0) {
      const filteredItems = availableGlossaryItems.filter(item => 
        item.blueprint_id && field.filterOptions?.includes(item.blueprint_id)
      );
      return filteredItems.length === 0; // No filtered items available
    }
  }
  
  // Check types fields
  if (field.type === 'types') {
    if (!availableTypes || availableTypes.length === 0) {
      return true; // No types available, treat as optional
    }
    
    // If field has filter options, check if any types match the filter
    if (field.filterOptions && field.filterOptions.length > 0) {
      const filteredTypes = availableTypes.filter(type => 
        field.filterOptions?.includes(type.id)
      );
      
      if (filteredTypes.length === 0) {
        return true; // No types match the filter
      }
      
      // Check if filtered types have any selectable elements
      const hasSelectableElements = filteredTypes.some(type => 
        type.data && type.data.length > 0
      );
      return !hasSelectableElements; // No selectable elements in filtered types
    }
    
    // No filter options, check if any types have selectable elements
    const hasSelectableElements = availableTypes.some(type => 
      type.data && type.data.length > 0
    );
    return !hasSelectableElements; // No selectable elements in any types
  }
  
  return false; // Field should remain required
};

/**
 * Validate form fields, treating dropdown fields with no options as optional
 */
export const validateFormFields = (
  sections: any[],
  availableGlossaryItems?: any[],
  availableTypes?: any[]
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  

  
  for (const section of sections) {
    
    for (const field of section.fields) {
      const shouldBeOptional = shouldTreatAsOptional(field, availableGlossaryItems, availableTypes);
      
      // Skip validation if field should be treated as optional
      if (field.required && !shouldBeOptional) {
        // For number fields, 0 is a valid value, so check more carefully
        const isEmpty = field.type === 'number'
          ? (field.value === undefined || field.value === null || field.value === '')
          : (!field.value || 
            (typeof field.value === 'string' && !field.value.trim()) ||
            (Array.isArray(field.value) && field.value.length === 0));
        
        // Additional check: if it's a dropdown/select field with no options, treat as optional
        const isDropdownWithNoOptions = (field.type === 'glossaryItem' || field.type === 'types') && 
          ((field.type === 'glossaryItem' && (!availableGlossaryItems || availableGlossaryItems.length === 0)) ||
           (field.type === 'types' && (!availableTypes || availableTypes.length === 0)));
          
        if (isEmpty && !isDropdownWithNoOptions) {
          errors.push(`${field.label} is required`);
        }
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Check if sections structure has changed
 */
export const hasSectionsStructureChanged = (
  prevSections: any[], 
  newSections: any[]
): boolean => {
  if (prevSections.length !== newSections.length) return true;

  for (let i = 0; i < newSections.length; i++) {
    const prevSection = prevSections[i];
    const newSection = newSections[i];

    if (!prevSection || !newSection) return true;
    if (prevSection.id !== newSection.id) return true;
    if (prevSection.fields.length !== newSection.fields.length) return true;

    for (let j = 0; j < newSection.fields.length; j++) {
      const prevField = prevSection.fields[j];
      const newField = newSection.fields[j];

      if (!prevField || !newField) return true;
      if (prevField.id !== newField.id) return true;
      if (prevField.multiple !== newField.multiple) return true;
    }
  }

  return false;
};
